import React from "react"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import Grid from "@material-ui/core/Grid"
import Button from "@material-ui/core/Button"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"

import Image from "./image"

export interface RoutineSummaryRow {
  level: string
  time: string
  focus: string
  weeklyHabit: string
}

export interface RoutineSection {
  title: string
  paragraphs: string[]
}

interface Props {
  instrument: string
  title: string
  intro: string
  image: string
  imageAlt: string
  sharedCalendarUrl?: string
  summary: RoutineSummaryRow[]
  sections: RoutineSection[]
}

const sectionSpacing = {
  marginTop: 32,
}

const PracticeRoutinePost = ({
  instrument,
  title,
  intro,
  image,
  imageAlt,
  sharedCalendarUrl,
  summary,
  sections,
}: Props) => (
  <Box component="article" style={{ paddingTop: 40, paddingBottom: 56 }}>
    <Button color="primary" href="/guides/?group=Activity%20routines&category=Music" style={{ marginBottom: 24 }}>
      Back to music routines
    </Button>

    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={7}>
        <Typography variant="overline" color="primary">
          {instrument} Practice
        </Typography>
        <Typography variant="h3" component="h1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {intro}
        </Typography>
        {sharedCalendarUrl && (
          <Box style={{ marginTop: 20 }}>
            <Button
              variant="contained"
              color="primary"
              href={sharedCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the shared weekly calendar
            </Button>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={5}>
        <Image alt={imageAlt} filename={image} />
      </Grid>
    </Grid>

    <Box style={sectionSpacing}>
      <Typography variant="body1" paragraph>
        Use these routines as a planning guide alongside your teacher's advice
        and the current AMEB syllabus. The exact technical work and repertoire
        will change by grade, but the weekly shape can stay simple and steady.
      </Typography>
    </Box>

    <Box style={sectionSpacing}>
      <Typography variant="h5" component="h2" gutterBottom>
        Quick practice summary
      </Typography>
      <Box style={{ overflowX: "auto" }}>
        <Table aria-label={`${instrument} AMEB practice routine summary`}>
          <TableHead>
            <TableRow>
              <TableCell>AMEB level</TableCell>
              <TableCell>Session length</TableCell>
              <TableCell>Main focus</TableCell>
              <TableCell>Weekly habit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.map(row => (
              <TableRow key={row.level}>
                <TableCell component="th" scope="row">
                  {row.level}
                </TableCell>
                <TableCell>{row.time}</TableCell>
                <TableCell>{row.focus}</TableCell>
                <TableCell>{row.weeklyHabit}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>

    {sections.map(section => (
      <Box style={sectionSpacing} key={section.title}>
        <Typography variant="h5" component="h2" gutterBottom>
          {section.title}
        </Typography>
        {section.paragraphs.map(paragraph => (
          <Typography variant="body1" paragraph key={paragraph}>
            {paragraph}
          </Typography>
        ))}
      </Box>
    ))}
  </Box>
)

export default PracticeRoutinePost
